import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import M from 'materialize-css';
import { toast } from '../../func.ts';
import moment from 'moment/moment';
import dataMatrixCodec from '../../utilities/dataMatrixCodec';
import Paginator from '../../components/layout/paginator.tsx';
import queryhooks from '../../common/queryHooks/index.ts';
import { useDebounceValue } from '../../common/useDebounce.ts';
import mutationHooks from '../../common/mutationHooks/index.ts';
import {
  QuarantineStatusAction,
  QuarantineStatusEnum,
} from '../../common/enums.ts';
import { ScannedBarcodeData, Toast } from '../../common/types/models.ts';
import ContextMenu from '../../components/ContextMenu.tsx';
import { BsThreeDots } from 'react-icons/bs';
import { HydratedQuarantine } from '../../common/types/compoundModels.ts';
import QuarantineFoundMedsModal from './QuarantineFoundMedsModal.tsx';
import ManualQuarantineScanModal from './ManualQuarantineScanModal.tsx';
import QuarantineStatusNotesModal from './QuarantineStatusNotesModal.tsx';
import useCurrentCustomerLocationId from '../../hooks/useCurrentCustomerLocation.ts';

const pageId = '_' + crypto.randomUUID();

export type BarcodeData = {
  'SERIAL NUMBER'?: string;
  'BATCH/LOT NUMBER'?: string;
  'EXPIRATION DATE (YYMMDD)'?: string;
  'GLOBAL TRADE ITEM NUMBER (GTIN)'?: string;
  'SERIAL SHIPPING CONTAINER CODE (SSCC)'?: string;
  unknown?: unknown;
};

const QuarantineMeds = () => {
  const { customerLocationId } = useCurrentCustomerLocationId();

  const initValues = () => ({
    serialNumber: '',
    lotNumber: '',
    status: 'ALL',
    gtin: '',
    ndc: '',
  });

  const ref = useRef({
    abortController: new AbortController(),
    values: initValues(),
  });

  const statusRef = useRef<HTMLSelectElement>(null);

  const [medMenuTrigger, setMedMenuTrigger] =
    useState<HTMLTableDataCellElement | null>(null);

  const [medMenuIsOpen, setMedMenuIsOpen] = useState(false);
  const pageSize = 10;
  const [values, setValues] = useState(initValues());
  const [pageNumber, setPageNumber] = useState(1);
  const [note, setNote] = useState<string>('');
  const [noteModalIsOpen, setNoteModalIsOpen] = useState<boolean>(false);
  const [quarantineFoundMedsModalIsOpen, setQuarantineFoundMedsModalIsOpen] =
    useState<boolean>(false);
  const [manualQuarantineModalIsOpen, setManualQuarantineModalIsOpen] =
    useState(false);

  const [action, setAction] = useState<QuarantineStatusAction>(
    QuarantineStatusAction.Scan
  );
  const [status, setStatus] = useState<QuarantineStatusEnum>(
    QuarantineStatusEnum.Quarantined
  );

  const [quarantineId, setQuarantineId] = useState<number | null>(null);

  const debouncedValues = useDebounceValue(values, 500);

  const focusInput = () => {
    const sgtinElement = document.querySelector<HTMLInputElement>(
      '#quarantine-med-barcode-scanner'
    );

    setTimeout(() => {
      sgtinElement?.select();
    }, 600);
  };

  useEffect(() => {
    if (manualQuarantineModalIsOpen) {
      focusInput();
    }
  }, [manualQuarantineModalIsOpen]);

  useEffect(() => {
    setPageNumber(1);
  }, [values]);

  const { mutate: quarantine } =
    mutationHooks.quarantine.useCreateOneQuarantine();

  const { data: medList, isPending: searching } =
    queryhooks.quarantine.useReadManyQuarantineBySearch(
      {
        values: {
          ...debouncedValues,
          customerLocationId: customerLocationId || 0,
        },
        paginationOptions: {
          pageNumber,
          pageSize,
        },
      },
      {
        enabled: Object.values(debouncedValues).some(val => Boolean(val)),
      }
    );

  const { data: customerLocations } =
    queryhooks.customerLocation.useReadAllByUserId();

  const customerLocationIds =
    customerLocations
      ?.map(cl => Number(cl.id))
      .filter(id => !Number.isNaN(id)) ?? [];

  const { data: foundMeds, isPending: isPendingReadyManyByCustomerLocationId } =
    queryhooks.quarantine.useReadManyByCustomerLocationId(
      {
        customerLocationIds,
      },
      {
        enabled: Boolean(customerLocationIds?.length),
      }
    );

  useEffect(() => {
    if (foundMeds?.length) {
      setQuarantineFoundMedsModalIsOpen(true);
    }
  }, [foundMeds]);

  const { mutate: createStatus, isPending: isPendingCreateStatus } =
    mutationHooks.quarantine.useCreateOneStatus();

  const isMutating = useMemo(
    () =>
      isPendingCreateStatus ||
      searching ||
      isPendingReadyManyByCustomerLocationId,
    [isPendingCreateStatus, searching, isPendingReadyManyByCustomerLocationId]
  );

  const createQuarantineStatus = () => {
    if (quarantineId) {
      const onSuccess = (data: Toast | null) => {
        if (data?.payload?.msg.includes('Med not found')) {
          if (
            window.confirm(
              'This Med does not appear on its associated Document, and so no EPCIS SGTIN can be marked as received. Would like like to continue anyway?'
            )
          ) {
            return createStatus(
              {
                quarantineId,
                status,
                action,
                note,
                force: true,
              },
              { onSuccess: () => null }
            );
          }
        }
      };

      createStatus({ quarantineId, status, action, note }, { onSuccess });
    }
  };

  const onChange: React.ChangeEventHandler<
    HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
  > = ({ target: { id, value } }) => setValues(p => ({ ...p, [id]: value }));

  const noteOnChange = ({
    target: { value },
  }: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setNote(value);

  const onScan = (data: BarcodeData) => {
    const { unknown } = data;
    const serialNumber = data[`SERIAL NUMBER`];
    const sscc = data[`SERIAL SHIPPING CONTAINER CODE (SSCC)`];

    if (sscc || unknown) {
      return toast('Cannot quarantine with given data.');
    }

    if (serialNumber) {
      const medData: ScannedBarcodeData = {
        lotNumber: data['BATCH/LOT NUMBER'] || '',
        expirationDate: data['EXPIRATION DATE (YYMMDD)'] || '',
        gtin: data['GLOBAL TRADE ITEM NUMBER (GTIN)'] || '',
        serialNumber: data['SERIAL NUMBER'] || '',
      };

      if (medData) {
        quarantine({
          ...medData,
          customerLocationId: customerLocationId || 0,
          note,
        });
      }
    }
  };

  useEffect(() => {
    ref.current.values = values;
  }, [values]);

  useEffect(() => {
    if (values.serialNumber && values.serialNumber.length > 20) {
      const data = dataMatrixCodec.decode(values.serialNumber) as BarcodeData;

      if (data['SERIAL NUMBER']) {
        setValues(p => ({ ...p, serialNumber: data['SERIAL NUMBER'] ?? '' }));
      } else {
        setValues(p => ({ ...p, serialNumber: '' }));
        toast({
          msg: 'Serial Number not found in barcode',
          class: 'orange white-text',
        });
      }
    }
  }, [values.serialNumber]);

  useLayoutEffect(() => {
    if (!statusRef.current) return;

    const instance = M.FormSelect.init(statusRef.current);

    return () => {
      instance.destroy();
    };
  }, []);

  return (
    <div>
      <div className="row">
        <div className=" col s3 m3 l3">
          <button
            onClick={() => setManualQuarantineModalIsOpen(true)}
            className="btn-small blue white-text waves-effect waves-light"
            style={{ marginTop: '10px' }}
          >
            Manual Scan
          </button>
        </div>
      </div>
      <div className="row">
        <div className="input-field col s6 m6 l6">
          <input
            id="serialNumber"
            className={`search-med-${pageId}`}
            type="text"
            value={values.serialNumber}
            onChange={onChange}
          />
          <label htmlFor="serialNumber">Serial #</label>
        </div>

        <div className="input-field col s6 m6 l6">
          <input
            id="gtin"
            className={`search-med-${pageId}`}
            type="text"
            value={values.gtin}
            onChange={onChange}
          />
          <label htmlFor="gtin">GTIN</label>
        </div>
      </div>
      <div className="row">
        <div className="input-field col s4 m4 l4">
          <input
            id="lotNumber"
            className={`search-med-${pageId}`}
            type="text"
            value={values.lotNumber}
            onChange={onChange}
          />
          <label htmlFor="lotNumber">Lot #</label>
        </div>
        <div className="input-field col s4 m4 l4">
          <input
            id="ndc"
            className={`search-med-${pageId}`}
            type="text"
            value={values.ndc}
            onChange={onChange}
          />
          <label htmlFor="ndc">NDC</label>
        </div>

        <div className="input-field col s4 m4 l4">
          <select
            ref={statusRef}
            id="status"
            name="status"
            onChange={onChange}
            value={values.status}
          >
            {[
              { label: 'ALL', value: 'ALL' },
              {
                label: 'Quarantined',
                value: QuarantineStatusEnum.Quarantined,
              },
              { label: 'Received', value: QuarantineStatusEnum.Received },
              {
                label: 'Illegitimate',
                value: QuarantineStatusEnum.Illegitimate,
              },
              {
                label: 'Unquarantined',
                value: QuarantineStatusEnum.Unquarantined,
              },
            ].map((option, index) => (
              <option key={index} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <label htmlFor="status">Status</label>
        </div>
      </div>
      <div className="row">
        {searching ? (
          <div className="progress">
            <div className="indeterminate" />
          </div>
        ) : (
          <>
            <table>
              <thead>
                <tr>
                  <th style={{ padding: '2px' }}>Serial #</th>
                  <th style={{ padding: '2px' }}>Lot Number</th>
                  <th style={{ padding: '2px' }}>GTIN</th>
                  <th style={{ padding: '2px' }}>Expiration Date</th>
                  <th style={{ padding: '2px' }}>Last Updated</th>
                  <th style={{ padding: '2px' }}>Note</th>
                  <th style={{ padding: '2px' }}>Status</th>
                  <th style={{ padding: '2px' }}></th>
                </tr>
              </thead>
              <tbody>
                {(medList?.data ?? []).map(
                  (row: HydratedQuarantine, index: number) => (
                    <tr key={row.id}>
                      <td style={{ padding: '2px' }}>{row.serialNumber}</td>
                      <td style={{ padding: '2px' }}>{row.lotNumber}</td>
                      <td style={{ padding: '2px' }}>{row.gtin}</td>

                      <td style={{ padding: '2px' }}>
                        {row.expirationDate
                          ? moment(row.expirationDate).format('MM/DD/YY')
                          : ''}
                      </td>

                      <td style={{ padding: '2px' }}>
                        {row.quarantineStatus?.createdBy?.firstName}{' '}
                        {row?.quarantineStatus.createdBy?.lastName}{' '}
                        {row.datetimeCreated
                          ? moment(row.datetimeCreated).format('MM/DD/YY')
                          : ''}
                      </td>

                      <td>
                        {row.quarantineStatus?.note ? (
                          row.quarantineStatus?.note
                        ) : (
                          <span>
                            <i>N/A</i>
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '2px' }}>
                        {row.quarantineStatus.status}
                      </td>

                      <td
                        style={{ cursor: 'pointer' }}
                        data-id={row.id}
                        onClick={e => {
                          e.stopPropagation();
                          setMedMenuTrigger(e.currentTarget);
                          setMedMenuIsOpen(prev =>
                            medMenuTrigger === e.currentTarget ? !prev : true
                          );
                        }}
                      >
                        <BsThreeDots className="right" />
                      </td>
                    </tr>
                  )
                )}
              </tbody>
            </table>
            <Paginator
              pageSize={pageSize}
              pageNumber={pageNumber}
              setPageNumber={setPageNumber}
              totalItems={medList?.total ?? 0}
            />
          </>
        )}
      </div>
      <ContextMenu
        triggerElement={medMenuTrigger}
        isOpen={medMenuIsOpen}
        setIsOpen={setMedMenuIsOpen}
        options={{
          Received: triggerElement => {
            const id = Number(triggerElement?.dataset.id);
            setNoteModalIsOpen(true);
            setQuarantineId(id);
            setAction(QuarantineStatusAction.Manual);
            setStatus(QuarantineStatusEnum.Received);
          },
          Quarantined: triggerElement => {
            const id = Number(triggerElement?.dataset.id);
            setNoteModalIsOpen(true);
            setQuarantineId(id);
            setAction(QuarantineStatusAction.Manual);
            setStatus(QuarantineStatusEnum.Quarantined);
          },
          Illegitimate: triggerElement => {
            const id = Number(triggerElement?.dataset.id);
            setNoteModalIsOpen(true);
            setQuarantineId(id);
            setAction(QuarantineStatusAction.Manual);
            setStatus(QuarantineStatusEnum.Illegitimate);
          },
          Returned: triggerElement => {
            const id = Number(triggerElement?.dataset.id);
            setNoteModalIsOpen(true);
            setQuarantineId(id);
            setAction(QuarantineStatusAction.Manual);
            setStatus(QuarantineStatusEnum.Returned);
          },
          Unquarantined: triggerElement => {
            const id = Number(triggerElement?.dataset.id);
            setNoteModalIsOpen(true);
            setQuarantineId(id);
            setAction(QuarantineStatusAction.Manual);
            setStatus(QuarantineStatusEnum.Unquarantined);
          },
        }}
      />

      <QuarantineStatusNotesModal
        isOpen={noteModalIsOpen}
        setIsOpen={setNoteModalIsOpen}
        note={note}
        setNote={setNote}
        onChange={noteOnChange}
        submit={createQuarantineStatus}
      />
      <QuarantineFoundMedsModal
        isOpen={quarantineFoundMedsModalIsOpen}
        setIsOpen={setQuarantineFoundMedsModalIsOpen}
        foundMeds={foundMeds}
      />
      <ManualQuarantineScanModal
        onScan={onScan}
        isMutating={isMutating}
        isOpen={manualQuarantineModalIsOpen}
        setIsOpen={setManualQuarantineModalIsOpen}
      />
    </div>
  );
};

export default QuarantineMeds;
